import React, { useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import { Bold, Italic, List, ListOrdered, Quote, Code, Heading1, Heading2, Heading3, Link as LinkIcon, Image as ImageIcon, Undo, Redo, Strikethrough } from "lucide-react";

/**
 * Modern React 19-compatible rich text editor built on TipTap / ProseMirror.
 * Stores content as HTML (matches the existing CMS backend contract).
 */
export default function RichTextEditor({ value, onChange, placeholder = "Start writing…", testId = "richtext" }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] }, link: false }),
      Link.configure({ openOnClick: false, autolink: true, HTMLAttributes: { rel: "noopener noreferrer nofollow", target: "_blank" } }),
      Image,
    ],
    content: value || "",
    editorProps: {
      attributes: {
        class: "tl-tiptap-content",
        "data-testid": `${testId}-content`,
      },
    },
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      // Emit empty string when Tiptap has no meaningful content (just an empty <p>)
      onChange(html === "<p></p>" ? "" : html);
    },
  });

  // Sync external value changes (e.g. loading an existing resource for edit).
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    const next = value || "";
    if (current !== next && !(current === "<p></p>" && next === "")) {
      editor.commands.setContent(next, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  if (!editor) return <div className="tl-tiptap-loading">Loading editor…</div>;

  const btn = (id, active, onClick, title, children) => (
    <button
      type="button"
      key={id}
      data-testid={`${testId}-btn-${id}`}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      title={title}
      className={`tl-tiptap-btn ${active ? "is-active" : ""}`}
    >{children}</button>
  );

  const linkPrompt = () => {
    const prev = editor.getAttributes("link").href;
    const url = window.prompt("URL (leave empty to remove)", prev || "https://");
    if (url === null) return;
    if (url === "") { editor.chain().focus().extendMarkRange("link").unsetLink().run(); return; }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };
  const imagePrompt = () => {
    const url = window.prompt("Image URL");
    if (url) editor.chain().focus().setImage({ src: url }).run();
  };

  return (
    <div className="tl-tiptap" data-testid={testId}>
      <div className="tl-tiptap-toolbar">
        {btn("h1", editor.isActive("heading", { level: 1 }), () => editor.chain().focus().toggleHeading({ level: 1 }).run(), "Heading 1", <Heading1 className="w-4 h-4" />)}
        {btn("h2", editor.isActive("heading", { level: 2 }), () => editor.chain().focus().toggleHeading({ level: 2 }).run(), "Heading 2", <Heading2 className="w-4 h-4" />)}
        {btn("h3", editor.isActive("heading", { level: 3 }), () => editor.chain().focus().toggleHeading({ level: 3 }).run(), "Heading 3", <Heading3 className="w-4 h-4" />)}
        <span className="tl-tiptap-sep" />
        {btn("bold", editor.isActive("bold"), () => editor.chain().focus().toggleBold().run(), "Bold", <Bold className="w-4 h-4" />)}
        {btn("italic", editor.isActive("italic"), () => editor.chain().focus().toggleItalic().run(), "Italic", <Italic className="w-4 h-4" />)}
        {btn("strike", editor.isActive("strike"), () => editor.chain().focus().toggleStrike().run(), "Strikethrough", <Strikethrough className="w-4 h-4" />)}
        <span className="tl-tiptap-sep" />
        {btn("ul", editor.isActive("bulletList"), () => editor.chain().focus().toggleBulletList().run(), "Bullet list", <List className="w-4 h-4" />)}
        {btn("ol", editor.isActive("orderedList"), () => editor.chain().focus().toggleOrderedList().run(), "Numbered list", <ListOrdered className="w-4 h-4" />)}
        <span className="tl-tiptap-sep" />
        {btn("quote", editor.isActive("blockquote"), () => editor.chain().focus().toggleBlockquote().run(), "Blockquote", <Quote className="w-4 h-4" />)}
        {btn("code", editor.isActive("codeBlock"), () => editor.chain().focus().toggleCodeBlock().run(), "Code block", <Code className="w-4 h-4" />)}
        <span className="tl-tiptap-sep" />
        {btn("link", editor.isActive("link"), linkPrompt, "Add / edit link", <LinkIcon className="w-4 h-4" />)}
        {btn("image", false, imagePrompt, "Insert image", <ImageIcon className="w-4 h-4" />)}
        <span className="tl-tiptap-sep" />
        {btn("undo", false, () => editor.chain().focus().undo().run(), "Undo", <Undo className="w-4 h-4" />)}
        {btn("redo", false, () => editor.chain().focus().redo().run(), "Redo", <Redo className="w-4 h-4" />)}
      </div>
      <EditorContent editor={editor} placeholder={placeholder} />
    </div>
  );
}
